/**
 * `/admin/:insee/inscriptions`: Inscriptions & relances (`docs/ADMIN.md`
 * §2) — is the registration funnel moving, what is stuck. One row per case,
 * open-first then oldest-first, with its derived follow-up sequence state.
 * Never an e-mail or a phone number: `has_email` is enough to coordinate.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getCommuneDashboard, getRegistrationCases } from '../../../api/admin';
import { Badge, Card, Skeleton } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { formatDate } from '../format';
import { CASE_STATUS_LABEL_KEYS, SEQUENCE_STATE_LABEL_KEYS, STEP_LABEL_KEYS } from '../labels';

import type { CommuneDashboard } from '../../../api/types';
import type { AdminRegistrationCase, RegistrationCaseSequence } from '../types';

/** The badge tone for a case's derived sequence state. */
function sequenceTone(
  state: RegistrationCaseSequence['state'],
): 'neutral' | 'green' | 'yellow' | 'red' {
  switch (state) {
    case 'running':
      return 'green';
    case 'replied':
      return 'green';
    case 'failed':
      return 'red';
    case 'unsubscribed':
      return 'yellow';
    default:
      return 'neutral';
  }
}

/** Render one case's `sequence` cell: the state badge, plus the next/last step when there is one. */
function SequenceCell({ sequence }: { sequence: RegistrationCaseSequence }) {
  return (
    <div className="admin-sequence-cell">
      <Badge tone={sequenceTone(sequence.state)}>
        {t(SEQUENCE_STATE_LABEL_KEYS[sequence.state])}
      </Badge>
      {sequence.state === 'running' && sequence.next_step && sequence.next_scheduled_for ? (
        <p className="admin-muted">
          {t('admin.inscriptions.nextScheduled', {
            step: t(STEP_LABEL_KEYS[sequence.next_step]),
            date: formatDate(sequence.next_scheduled_for),
          })}
        </p>
      ) : null}
      {sequence.state !== 'running' && sequence.last_sent_step && sequence.last_sent_at ? (
        <p className="admin-muted">
          {t('admin.inscriptions.lastSent', {
            step: t(STEP_LABEL_KEYS[sequence.last_sent_step]),
            date: formatDate(sequence.last_sent_at),
          })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Render the Inscriptions & relances screen.
 *
 * @returns The page element.
 */
export function InscriptionsPage() {
  const { insee = '' } = useParams<{ insee: string }>();
  const [cases, setCases] = useState<AdminRegistrationCase[] | undefined>(undefined);
  const [dashboard, setDashboard] = useState<CommuneDashboard | undefined>(undefined);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCases(undefined);
    setDashboard(undefined);
    setLoadError(false);
    Promise.all([getRegistrationCases(insee), getCommuneDashboard(insee)])
      .then(([result, communeDashboard]) => {
        if (cancelled) return;
        setCases(result.results);
        setDashboard(communeDashboard);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [insee]);

  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <h1>{t('admin.inscriptions.title')}</h1>
        <p className="admin-lead">{t('admin.inscriptions.lead')}</p>
      </div>

      {dashboard ? (
        <>
          <Card className="admin-block">
            <p className="admin-kicker">{t('admin.inscriptions.funnel.kicker')}</p>
            <div className="admin-kpis">
              <div className="admin-kpi">
                <p className="admin-kpi__value">{dashboard.registration_funnel.info_given}</p>
                <p className="admin-kpi__label">{t('admin.inscriptions.funnel.infoGiven')}</p>
              </div>
              <div className="admin-kpi">
                <p className="admin-kpi__value">{dashboard.registration_funnel.filed}</p>
                <p className="admin-kpi__label">{t('admin.inscriptions.funnel.filed')}</p>
              </div>
              <div className="admin-kpi">
                <p className="admin-kpi__value">{dashboard.registration_funnel.confirmed}</p>
                <p className="admin-kpi__label">{t('admin.inscriptions.funnel.confirmed')}</p>
              </div>
              <div className="admin-kpi">
                <p className="admin-kpi__value">{dashboard.registration_funnel.closed}</p>
                <p className="admin-kpi__label">{t('admin.inscriptions.funnel.closed')}</p>
              </div>
            </div>
          </Card>

          <Card className="admin-block">
            <p className="admin-kicker">{t('admin.inscriptions.sequenceFunnel.kicker')}</p>
            <p className="admin-lead">{t('admin.inscriptions.sequenceFunnel.lead')}</p>
            <div className="admin-kpis">
              <div className="admin-kpi">
                <p className="admin-kpi__value">{dashboard.followups.sent_7d}</p>
                <p className="admin-kpi__label">{t('admin.inscriptions.sequenceFunnel.sent7d')}</p>
              </div>
              <div className="admin-kpi">
                <p className="admin-kpi__value">{dashboard.followups.scheduled}</p>
                <p className="admin-kpi__label">
                  {t('admin.inscriptions.sequenceFunnel.scheduled')}
                </p>
              </div>
              <div className="admin-kpi">
                <p className="admin-kpi__value">{dashboard.followups.replied}</p>
                <p className="admin-kpi__label">{t('admin.inscriptions.sequenceFunnel.replied')}</p>
              </div>
              <div className="admin-kpi">
                <p className="admin-kpi__value">{dashboard.followups.unsubscribed}</p>
                <p className="admin-kpi__label">
                  {t('admin.inscriptions.sequenceFunnel.unsubscribed')}
                </p>
              </div>
              <div
                className={
                  dashboard.followups.failed > 0 ? 'admin-kpi admin-kpi--alerte' : 'admin-kpi'
                }
              >
                <p className="admin-kpi__value">{dashboard.followups.failed}</p>
                <p className="admin-kpi__label">{t('admin.inscriptions.sequenceFunnel.failed')}</p>
              </div>
            </div>
            <p className="admin-muted">{t('admin.inscriptions.sequenceFunnel.noOpenRate')}</p>
          </Card>
        </>
      ) : null}

      {loadError ? (
        <p role="alert" className="admin-error">
          {t('admin.inscriptions.loadError')}
        </p>
      ) : cases === undefined ? (
        <>
          <Skeleton height="3rem" />
          <Skeleton height="3rem" />
          <Skeleton height="3rem" />
        </>
      ) : cases.length === 0 ? (
        <p className="admin-muted">{t('admin.inscriptions.empty')}</p>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">{t('admin.inscriptions.table.firstName')}</th>
                <th scope="col">{t('admin.inscriptions.table.door')}</th>
                <th scope="col">{t('admin.inscriptions.table.status')}</th>
                <th scope="col">{t('admin.inscriptions.table.openedAt')}</th>
                <th scope="col">{t('admin.inscriptions.table.volunteer')}</th>
                <th scope="col">{t('admin.inscriptions.table.sequence')}</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((registrationCase) => (
                <tr key={registrationCase.id}>
                  <td>{registrationCase.first_name}</td>
                  <td>{registrationCase.door_label}</td>
                  <td>{t(CASE_STATUS_LABEL_KEYS[registrationCase.status])}</td>
                  <td>{formatDate(registrationCase.opened_at)}</td>
                  <td>
                    {registrationCase.volunteer.first_name} {registrationCase.volunteer.last_name}
                  </td>
                  <td>
                    {registrationCase.has_email ? (
                      <SequenceCell sequence={registrationCase.sequence} />
                    ) : (
                      <Badge tone="neutral">{t('admin.inscriptions.sequence.none')}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
